import Component from '@glimmer/component';
import { service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { PhCopySimple } from 'ember-phosphor-icons';
import Button from 'tio-ui/components/button';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type SessionContextService from 'tio-common/services/session-context';
import type { ReportsExtension as Report } from 'tio-data/data/resources/reports.ext';
import type ReportDuplicationService from '../services/report-duplication.ts';

interface DuplicateReportButtonSignature {
  Args: {
    /** Saved source identity; unsaved values are never sent when creating the copy. */
    report: Report;
    /** Uses persisted attributes; also rechecked against a fresh, unhydrated source response. */
    isEligible: (report: Report) => boolean;
    /** Partner company lock; omit for staff copies with no partner audience. */
    lockedCompanyId?: string;
    /** Called after creation only while the initiating report and session are still current. */
    onCopied: (copy: Report) => void | Promise<void>;
    /** Include visible text beside the icon. */
    showLabel?: boolean;
    appearance?: 'minimal' | 'outlined';
    /** Disables duplication for an unsaved or otherwise unavailable builder state. */
    isDisabled?: boolean;
    /** Changing this value abandons the initiating view before POST or its completion callback. */
    contextKey?: string | number;
  };
  Element: HTMLButtonElement | HTMLAnchorElement;
}

export default class DuplicateReportButton extends Component<DuplicateReportButtonSignature> {
  @service declare reportDuplication: ReportDuplicationService;
  @service declare sessionContext: SessionContextService;
  @service declare notifications: NotificationsService;

  get isEligible() {
    return Boolean(this.args.report?.id && this.args.isEligible(this.args.report));
  }

  get isDisabled() {
    return Boolean(
      this.args.isDisabled ||
      !this.sessionContext.user?.id ||
      this.reportDuplication.duplicate.isRunning
    );
  }

  get ariaLabel() {
    return `Duplicate ${this.args.report.name || 'report'}`;
  }

  duplicate = dropTask(async () => {
    if (!this.isEligible || this.isDisabled) return;
    const sourceId = this.args.report.id;
    if (!sourceId) return;
    const ownerId = this.sessionContext.user?.id;
    const lockedCompanyId = this.args.lockedCompanyId;
    const contextKey = this.args.contextKey;
    const canProceed = () =>
      !this.isDestroying &&
      !this.isDestroyed &&
      this.args.report.id === sourceId &&
      this.sessionContext.user?.id === ownerId &&
      this.args.lockedCompanyId === lockedCompanyId &&
      this.args.contextKey === contextKey &&
      !this.args.isDisabled;
    const result = await this.reportDuplication.duplicate.unlinked().perform(sourceId, {
      isEligible: this.args.isEligible,
      lockedCompanyId,
      canProceed,
    });
    if (!result || !canProceed()) return;
    try {
      await this.args.onCopied(result.report);
    } catch {
      this.notifications.add('Your copy was created. Refresh saved reports to open it.', {
        appearance: 'warning',
      });
    }
  });

  <template>
    {{#if this.isEligible}}
      <Button
        @appearance={{if @appearance @appearance "minimal"}}
        @intent="default"
        @size="sm"
        @icon={{PhCopySimple}}
        @isRunning={{this.duplicate.isRunning}}
        @isDisabled={{this.isDisabled}}
        @ariaLabel={{this.ariaLabel}}
        title="Duplicate saved report"
        {{on "click" this.duplicate.perform}}
        data-test-duplicate-report-action={{@report.id}}
        ...attributes
      >
        {{#if @showLabel}}Duplicate{{/if}}
      </Button>
    {{/if}}
  </template>
}
